
import React, { useState } from 'react';
import { MessageSquare, Send, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { TAB_CONTAINER_STYLES } from './shared-styles';

interface Comment {
  id: string;
  content: {
    comment_text: string;
    category?: string;
  };
  submitted_at: string;
}

interface CommentFormProps {
  comments: Comment[];
  onSubmitComment: (comment: string, category?: string) => Promise<void>;
  loading: boolean;
}

export const CommentForm: React.FC<CommentFormProps> = ({
  comments,
  onSubmitComment,
  loading
}) => {
  const [comment, setComment] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('general');

  const categories = [
    { value: 'general', label: 'General', color: 'bg-peach-orange/10 text-peach-orange border-peach-orange/20' },
    { value: 'correction', label: 'Correction', color: 'bg-coral-pink/10 text-coral-pink border-coral-pink/20' },
    { value: 'hours', label: 'Opening Hours', color: 'bg-sunset-yellow/10 text-sunset-yellow border-sunset-yellow/20' },
    { value: 'accessibility', label: 'Accessibility', color: 'bg-dusty-rose/10 text-dusty-rose border-dusty-rose/20' },
    { value: 'pricing', label: 'Pricing', color: 'bg-light-coral/10 text-light-coral border-light-coral/20' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    await onSubmitComment(comment.trim(), selectedCategory);
    setComment('');
  };

  return (
    <div className="space-y-6">
      {/* Comment form */}
      <div className="bg-warm-cream/30 rounded-lg p-5 border border-sunset-medium/10">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-coral-pink/10">
            <MessageSquare className="h-5 w-5 text-coral-pink" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-sunset-dark">Share Feedback</h3>
            <p className="text-sm text-sunset-medium">Help improve this terrace information</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={`space-y-5 ${TAB_CONTAINER_STYLES}`}>
          <div className="space-y-4">
            <Label className="text-base font-semibold text-sunset-dark flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Category
            </Label>
            <RadioGroup 
              value={selectedCategory} 
              onValueChange={setSelectedCategory}
              className="grid grid-cols-2 md:grid-cols-3 gap-3"
            >
              {categories.map((category) => (
                <div key={category.value} className="relative">
                  <RadioGroupItem 
                    value={category.value} 
                    id={category.value}
                    className="peer sr-only"
                  />
                  <Label 
                    htmlFor={category.value} 
                    className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all hover:scale-105 peer-checked:ring-2 peer-checked:ring-peach-orange peer-checked:ring-offset-2 ${category.color}`}
                  >
                    <span className="text-sm font-medium">{category.label}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label htmlFor="comment" className="text-base font-semibold text-sunset-dark">
              Your feedback
            </Label>
            <Textarea
              id="comment"
              placeholder="Share your feedback, corrections, or suggestions..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[120px] resize-none text-base"
              rows={5}
            />
          </div>

          <Button 
            type="submit" 
            disabled={!comment.trim() || loading}
            variant="action"
            size="lg"
          >
            <Send className="h-5 w-5 mr-2" />
            Submit Feedback
          </Button>
        </form>
      </div>

      {/* Recent comments */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-sunset-dark">Recent Feedback</h4>
          <Badge variant="secondary" className="bg-coral-pink/10 text-coral-pink border-coral-pink/20">
            {comments.length} comments
          </Badge>
        </div>
        
        {comments.length > 0 ? (
          <div className="space-y-4">
            {comments.slice(0, 5).map((feedback) => {
              const category = categories.find(c => c.value === feedback.content.category);
              return (
                <Card key={feedback.id} className="p-4 border-sunset-medium/20 hover:shadow-md transition-shadow">
                  <div className="space-y-3">
                    <p className="text-sunset-dark leading-relaxed">{feedback.content.comment_text}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {category && (
                          <Badge className={category.color}>
                            {category.label}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-sunset-medium">
                        {new Date(feedback.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-warm-cream/20 rounded-lg border-2 border-dashed border-sunset-medium/30">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-sunset-medium/50" />
            <h5 className="text-lg font-medium text-sunset-dark mb-2">No feedback yet</h5>
            <p className="text-sunset-medium">Be the first to share your thoughts!</p>
          </div>
        )}
      </div>
    </div>
  );
};
